import { useState, useMemo } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { comics, categories } from '@/mocks/comics';
import useScrollToTop from '@/hooks/useScrollToTop';

const ITEMS_PER_PAGE = 8;

type SortKey = 'popular' | 'rating' | 'updated';

export default function CategoryPage() {
  useScrollToTop();
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';

  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<SortKey>('popular');
  const [activeCategory, setActiveCategory] = useState(slug || 'all');

  const categoryInfo = categories.find(c => c.slug === activeCategory) || categories[0];

  const filteredComics = useMemo(() => {
    let result = activeCategory === 'all' ? [...comics] : comics.filter(c => c.category === activeCategory);

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        c => c.title.toLowerCase().includes(q) ||
             c.author.toLowerCase().includes(q) ||
             c.tags.some(t => t.toLowerCase().includes(q))
      );
    }

    switch (sortBy) {
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'updated':
        result.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        break;
      case 'popular':
      default:
        result.sort((a, b) => b.views - a.views);
        break;
    }

    return result;
  }, [activeCategory, sortBy, searchQuery]);

  const totalPages = Math.ceil(filteredComics.length / ITEMS_PER_PAGE);
  const displayComics = filteredComics.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const handleCategoryChange = (catSlug: string) => {
    setActiveCategory(catSlug);
    setCurrentPage(1);
  };

  const pageTitle = searchQuery
    ? `搜索"${searchQuery}"的韩漫结果 - 韩漫之家`
    : `${categoryInfo.name}韩漫_免费${categoryInfo.name}类韩漫在线观看 - 韩漫之家`;

  const pageDescription = searchQuery
    ? `在韩漫之家搜索"${searchQuery}"的韩漫结果，海量免费韩漫在线观看。`
    : `${categoryInfo.description}。韩漫之家提供免费${categoryInfo.name}韩漫在线观看，下拉式流畅阅读体验。`;

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: pageDescription,
    url: `https://www.manhuazhijia.com/category/${activeCategory}`,
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        keywords={`${categoryInfo.name}韩漫,免费韩漫,韩漫在线观看,下拉式韩漫,韩漫之家`}
        canonicalUrl={`https://www.manhuazhijia.com/category/${activeCategory}`}
        schemaData={[webPageSchema]}
      />
      <Navbar />
      <main>
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            ...(searchQuery ? [{ label: `搜索: ${searchQuery}` }] : [{ label: categoryInfo.name, href: `/category/${activeCategory}` }]),
          ]}
        />

        <section className="w-full px-4 md:px-6 py-8 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="mb-8">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                {searchQuery ? `搜索: "${searchQuery}"` : `${categoryInfo.name}韩漫`}
              </h1>
              {!searchQuery && (
                <p className="text-foreground-500 text-sm mt-2">{categoryInfo.description}</p>
              )}
              {searchQuery && filteredComics.length > 0 && (
                <p className="text-foreground-500 text-sm mt-2">
                  找到 {filteredComics.length} 部相关韩漫作品
                </p>
              )}
              {searchQuery && filteredComics.length === 0 && (
                <p className="text-foreground-500 text-sm mt-2">
                  未找到相关韩漫，请尝试其他关键词
                </p>
              )}
            </div>

            {!searchQuery && (
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {categories.map((cat) => (
                    <button
                      key={cat.slug}
                      onClick={() => handleCategoryChange(cat.slug)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                        activeCategory === cat.slug
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-xs text-foreground-400 whitespace-nowrap">排序：</span>
                  {[
                    { key: 'popular' as SortKey, label: '最热门' },
                    { key: 'rating' as SortKey, label: '评分最高' },
                    { key: 'updated' as SortKey, label: '最近更新' },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => { setSortBy(opt.key); setCurrentPage(1); }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                        sortBy === opt.key
                          ? 'bg-secondary-200 text-secondary-800'
                          : 'text-foreground-500 hover:bg-background-100'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {displayComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-5" data-product-shop>
                {displayComics.map((comic) => (
                  <ComicCard key={comic.id} {...comic} coverImage={comic.coverImage} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                  <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500 text-sm">暂无相关漫画内容</p>
              </div>
            )}

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200/70 text-foreground-500 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                      currentPage === page
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="w-9 h-9 flex items-center justify-center rounded-lg border border-background-200/70 text-foreground-500 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}